// Thumbnail rail of the source viewer: the pages around the current one (p−2 to p+2) rendered small from the PDF, the current page outlined in red with one tiny mark per highlighted line (clause and requirement lines that coincide share a mark), each a button that opens its page.
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/cn";

import type { ViewerMarks } from "./highlights";
import { outputRatio, usePdfPage } from "./PdfPage";

const THUMB_WIDTH = 54;
const RADIUS = 2;

export function thumbnailMarkTops(marks: ViewerMarks | null): number[] {
  if (marks === null || !(marks.pageHeight > 0)) {
    return [];
  }
  const tops = new Set(
    [...marks.clause, ...marks.text].map((rect) => Math.round((rect.y0 / marks.pageHeight) * 1000) / 10),
  );
  return [...tops].sort((a, b) => a - b);
}

export function thumbnailPages(page: number, numPages: number): number[] {
  const first = Math.max(1, Math.min(page - RADIUS, numPages - 2 * RADIUS));
  const last = Math.min(numPages, first + 2 * RADIUS);
  return Array.from({ length: last - first + 1 }, (_, index) => first + index);
}

function ThumbCanvas({ doc, pageNumber }: { doc: PDFDocumentProxy; pageNumber: number }) {
  const { page } = usePdfPage(doc, pageNumber);
  const host = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const target = host.current;
    if (page === null || target === null) {
      return undefined;
    }
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: THUMB_WIDTH / base.width });
    const ratio = outputRatio(viewport, window.devicePixelRatio);
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    const task = page.render({
      canvas,
      viewport,
      ...(ratio === 1 ? {} : { transform: [ratio, 0, 0, ratio, 0, 0] }),
    });
    let cancelled = false;
    task.promise.then(
      () => {
        if (!cancelled) {
          target.replaceChildren(canvas);
        }
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [page]);
  return <span ref={host} className="block aspect-[0.707] w-full" />;
}

interface ThumbnailsProps {
  doc: PDFDocumentProxy;
  page: number;
  numPages: number;
  marks: ViewerMarks | null;
  onSelect: (page: number) => void;
  className?: string;
}

export function Thumbnails({ doc, page, numPages, marks, onSelect, className }: ThumbnailsProps) {
  const tops = thumbnailMarkTops(marks);
  return (
    <nav
      aria-label="Pages"
      className={cn(
        "flex w-[78px] shrink-0 scrollbar-thin flex-col items-center gap-3 overflow-auto border-r border-viewer-border bg-viewer-rail px-2.5 py-3.5",
        className,
      )}
    >
      <ol className="m-0 flex list-none flex-col items-center gap-3 p-0">
        {thumbnailPages(page, numPages).map((number) => (
          <li key={number} className="flex flex-col items-center gap-1">
            <button
              type="button"
              className="pdf-thumb"
              aria-label={`Page ${String(number)}`}
              aria-current={number === page ? "page" : undefined}
              onClick={() => {
                onSelect(number);
              }}
            >
              <ThumbCanvas doc={doc} pageNumber={number} />
              {number === page
                ? tops.map((top) => (
                    <i key={top} aria-hidden className="pdf-thumb__mark" style={{ top: `${String(top)}%` }} />
                  ))
                : null}
            </button>
            <span aria-hidden className="text-micro text-viewer-text tabular-nums">
              {number}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}
